import React, { useEffect, useState, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment';
import throttle from 'lodash/throttle';
import { isAuthenticated } from '../../../../services/auth';
import { getUsers, deleteUser, banUser, unBanUser, changeUserPassword } from '../../../../services/admin';
import { Avatar, message, Popconfirm, Badge, Input, Modal, Form } from 'antd';
import { UserOutlined, EditOutlined, LockOutlined, UnlockOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import UnifiedDashboard from '../../../../components/layout/UnifiedDashboard';
import OptimizedList from '../../../../components/ui/lists/OptimizedList';
import './ManageUsers.css';

const { Search } = Input;

const ManageUsers = () => {
  const countPerPage = 10;
  const [users, setUsers] = useState({
    userList: [],
    pagination: [],
    deleted: false,
    banned: false,
  });
  const [current, setCurrent] = useState();
  const [search, setSearch] = useState('');
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [passwordForm] = Form.useForm();
  const [loadingPassword, setLoadingPassword] = useState(false);

  const { userList, pagination, deleted, banned } = users;
  const { user, token } = isAuthenticated();
  const history = useHistory();

  const searchData = useRef(
    throttle((val) => {
      const query = val.toLowerCase();
      setUsers({
        ...users,
        pagination: userList.filter((c) => {
          return c.name.toLowerCase().indexOf(query) > -1;
        })
      });
    }, 400)
  );

  useEffect(() => {
    if (!search) {
      loadUsers(1);
    } else {
      searchData.current(search);
    }
  }, [search, deleted, banned]);

  const loadUsers = async (page) => {
    const res = await getUsers();
    setCurrent(page);
    const to = page * countPerPage;
    const from = to - countPerPage;
    setUsers({ userList: res.data, pagination: res.data.slice(from, to) });
  };

  const handleBan = async (userId) => {
    try {
      const adminRole = user.role;
      await banUser(userId, { adminRole, token });
      message.success('Usuário banido', 4);
      setUsers({ ...users, banned: false ? true : false });
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) message.error(err.response.data?.error || err.response.data, 4);
    }
  };

  const handleUnBan = async (userId) => {
    try {
      const adminRole = user.role;
      await unBanUser(userId, { adminRole, token });
      message.success('Usuário desbanido', 4);
      setUsers({ ...users, banned: true ? false : true });
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) message.error(err.response.data?.error || err.response.data, 4);
    }
  };

  const handleDelete = async (userId) => {
    try {
      const adminRole = user.role;
      await deleteUser(userId, { adminRole, token });
      message.success('Usuário deletado', 4);
      setUsers({ ...users, deleted: false ? true : false });
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) message.error(err.response.data?.error || err.response.data, 4);
    }
  };

  const openChangePasswordModal = (userItem) => {
    setSelectedUser(userItem);
    setChangePasswordModal(true);
    passwordForm.resetFields();
  };

  const handleChangePassword = async (values) => {
    try {
      setLoadingPassword(true);
      await changeUserPassword(selectedUser._id, {
        password: values.password,
        newPasswordConfirm: values.newPasswordConfirm
      });
      message.success('Senha alterada com sucesso', 4);
      setChangePasswordModal(false);
      passwordForm.resetFields();
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) {
        message.error(err.response.data?.error || err.response.data, 4);
      } else {
        message.error('Erro ao alterar senha', 4);
      }
    } finally {
      setLoadingPassword(false);
    }
  };

  const renderUserCard = (userItem, index) => {
    const c = userItem;
    const api = process.env.REACT_APP_API?.replace(/\/api$/, '');
    
    return (
      <div key={index} className='manage-users-user-card'>
        <div className='manage-users-card-header'>
          <div className='manage-users-user-header'>
            <Avatar 
              size={60}
              src={c.photo ? `${api}${c.photo}` : null}
              icon={<UserOutlined />}
              className="manage-users-avatar"
            />
            <div className="manage-users-user-details">
              <Link to={`/user/${c._id}`} className='manage-users-user-name'>
                {c.name}
              </Link>
              {c.role === 'banned' && (
                <Badge status="error" text="Banido" className='manage-users-badge' />
              )}
              <div className='manage-users-user-meta'>
                <small><i className='fas fa-envelope'></i> {c.email}</small>
              </div>
              <div className='manage-users-user-meta'>
                <small><i className='fas fa-calendar'></i> {moment(c.createdAt).format('DD/MM/YYYY')}</small>
              </div>
            </div>
          </div>
        </div>

        <div className='manage-users-card-actions'>
          <button 
            className='manage-users-action-btn manage-users-btn-edit'
            onClick={() => history.push(`/admin/user/edit/${c._id}`)}
            title="Editar usuário"
          >
            <EditOutlined /> Editar
          </button>
          <button 
            className='manage-users-action-btn manage-users-btn-product' 
            onClick={() => history.push(`/admin/add-product/${c._id}`)}
            title="Adicionar produto"
          >
            <PlusOutlined /> Produto
          </button>
          <button
            className='manage-users-action-btn manage-users-btn-password'
            onClick={() => openChangePasswordModal(c)}
            title="Trocar senha"
          >
            <LockOutlined /> Senha
          </button>
          {c.role === 'banned' ? (
            <Popconfirm 
              title={`Desbanir ${c.name}?`} 
              onConfirm={() => handleUnBan(c._id)}
              okText='Sim' 
              cancelText='Não'
            >
              <button className='manage-users-action-btn manage-users-btn-unban'>
                <UnlockOutlined /> Desbanir
              </button>
            </Popconfirm>
          ) : (
            <Popconfirm 
              title={`Banir ${c.name}?`} 
              onConfirm={() => handleBan(c._id)}
              okText='Sim' 
              cancelText='Não'
            >
              <button className='manage-users-action-btn manage-users-btn-ban'>
                <LockOutlined /> Banir
              </button>
            </Popconfirm>
          )}
          <Popconfirm 
            title={`Deletar ${c.name}?`} 
            onConfirm={() => handleDelete(c._id)}
            okText='Sim' 
            cancelText='Não'
          >
            <button className='manage-users-action-btn manage-users-btn-delete'>
              <DeleteOutlined /> Deletar
            </button>
          </Popconfirm>
        </div>
      </div>
    );
  };

  return (
    <UnifiedDashboard>
      <div className="manage-users-container">
        <div className='manage-users-header'>
          <div className='manage-users-header-content'>
            <h2 className='manage-users-title'>
              <i className='fas fa-users'></i>
              Gerenciar Usuários
            </h2>
            <p className='manage-users-subtitle'>Administre usuários do sistema</p>
          </div>
          <button 
            className='manage-users-add-user-btn'
            onClick={() => history.push('/admin/user/edit/new')}
            title="Cadastrar novo usuário"
          >
            <PlusOutlined /> Cadastrar Usuário
          </button>
        </div>

        <div className='manage-users-search-wrapper'>
          <Search
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="manage-users-search"
            prefix={<SearchOutlined />}
            size="large"
          />
        </div>

        <OptimizedList
          data={pagination || []}
          loading={false}
          pageSize={countPerPage}
          currentPage={current}
          total={userList.length}
          onPageChange={loadUsers}
          renderItem={renderUserCard}
          emptyText="Nenhum usuário encontrado"
          className="manage-users-list"
        />
      </div>

      <Modal
        title={`Trocar senha - ${selectedUser?.name}`}
        visible={changePasswordModal}
        onCancel={() => {
          setChangePasswordModal(false);
          passwordForm.resetFields();
        }}
        okText="Alterar"
        cancelText="Cancelar"
        onOk={() => passwordForm.submit()}
        confirmLoading={loadingPassword}
        width={500}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
          className="manage-users-password-form"
        >
          <Form.Item
            label="Nova Senha"
            name="password"
            rules={[
              { required: true, message: 'Senha é obrigatória' },
              { min: 6, message: 'Senha deve ter no mínimo 6 caracteres' },
              {
                pattern: /\d/,
                message: 'Senha deve conter pelo menos um número'
              }
            ]}
          >
            <Input.Password placeholder="Digite a nova senha" />
          </Form.Item>
          <Form.Item
            label="Confirmar Senha"
            name="newPasswordConfirm"
            rules={[
              { required: true, message: 'Confirmação de senha é obrigatória' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('As senhas não correspondem'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirme a nova senha" />
          </Form.Item>
        </Form>
      </Modal>
    </UnifiedDashboard>
  );
};

export default ManageUsers;
